<script setup lang="ts">
import {
  useSupabaseClient,
  useI18n,
  useDialog,
  ref,
  useAsyncData,
  defineAsyncComponent,
  useToast,
  useAuth
} from '#imports';
import type { Tables } from '~~/types/database.types.ts';
const supabase = useSupabaseClient();
const { t } = useI18n();
const { openDialog } = useDialog();
const { showError } = useToast();
const { currentUser } = useAuth()

const isLoading = ref(false)

const props = defineProps<{
  creator: Tables<'profiles'>,
  iconOnly?: boolean,
  size?: 'small' | 'medium' | 'large',
  bgColor?: string
}>();

const { data: followData, refresh: refreshFollowData } = await useAsyncData(
  async () => {
    isLoading.value = true;
    if (props.creator.id && currentUser.value?.profile) {
      const { data, error } = await supabase
        .from('followers')
        .select('*')
        .eq('user_id', currentUser.value.profile.id)


      if (error) {
        if (error.code !== 'PGRST116')
          showError(t('messages.maps.errorLoading'));
        return null;
      }

      if (data.length === 0) {
        isLoading.value = false;
        return null;
      }
      isLoading.value = false;
      return data[0];
    }
    isLoading.value = false;
  }
);

const handleFollowOperation = async (operation: 'insert' | 'delete') => {
  if (!currentUser.value) {
    openDialog('MPAuth', { startOn: 'login' });
    return;
  }

  if (!currentUser.value.profile) return;
  const payload = { user_id: currentUser.value.profile.id, creator_id: props.creator.id }

  const query = supabase.from('followers')[operation](payload);

  if (operation === 'delete') {
    query.eq('user_id', currentUser.value.profile.id).eq('creator_id', props.creator.id);
  }

  const { error } = await query;

  if (error) {
    showError(
      t(
        `messages.likes.error-${operation === 'insert' ? 'adding' : 'removing'}-like`,
      ),
    );
  }

  refreshFollowData();
};

const addFollow = () => handleFollowOperation('insert');
const removeFollow = () => handleFollowOperation('delete');


</script>

<template>
  <MPButton
    v-if="iconOnly"
    rounded
    class="follow-button"
    label="Follow"
    hide-label
    :background-color="props.bgColor"
    :size="props.size"
    :disabled="isLoading"
    icon="ri-user-add-line"
    @click="addFollow"
  />
  <MPButton
    v-else-if="iconOnly  && !followData"
    rounded
    class="follow-button"
    label="Unfolow"
    hide-label
    :background-color="props.bgColor"
    :size="props.size"
    :disabled="isLoading"
    icon="ri-user-add-line"
    @click="addFollow"
  />
  <MPButton
    v-else-if="!iconOnly && !followData"
    :label="isLoading ? 'Loading...' : 'Follow'"
    class="follow-button"
    version="secondary"
    :size="props.size"
    :background-color="props.bgColor"
    rounded
    :disabled="isLoading"
    aria-label="Follow"
    @click="addFollow"
  />
  <MPButton
    v-else-if="!iconOnly && followData"
    :label="isLoading ? 'Loading...' : 'Unfollow'"
    class="follow-button"
    version="secondary"
    :disabled="isLoading"
    :size="props.size"
    :background-color="props.bgColor"
    rounded
    aria-label="Follow"
    @click="removeFollow"
  />
  <!-- <MPTooltip
    v-if="props.tooltip"
    :content="toolTipContent"
    mode="fade"
    :show="showTooltip"
    class="tooltip-placement"
  /> -->
</template>
